import {
  Component,
  Input,
  forwardRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
  NgZone,
  Attribute,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  Output,
  HostBinding
} from '@angular/core'

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms'

export const VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DropdownComponent),
  multi: true,
}

export interface DropdownOptions {
  name: string
  value: any
  image?: string
  icon?: string
  color?: string
  optgroup?: string
  class?: string
  description?: string
  descriptionIcon?: string
}

@Component({
  selector: 'dropdown',
  host: {
    class: 'ui dropdown'
  },
  styleUrls: ['dropdown.component.less'],
  templateUrl: 'dropdown.component.html',
  providers: [
    VALUE_ACCESSOR
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DropdownComponent implements ControlValueAccessor, OnChanges, AfterViewInit {
  @Input() ngModel: any
  @Input() label: string
  @Input() @HostBinding('class.loading') loading: boolean
  @Input() loadingOptions: boolean = false;
  @Input() setDisabled: boolean = false;
  @Output() modelChange = new EventEmitter<any>()

  @Input() options: any[]
  @Input() editableOptions: any[]
  @Input() settings: any
  @Input() placeholder: string
  @Input() dropdownActiveClass: string

  $element: any
  dropdownSettings: any
  items: any[]
  defaultSelected: any
  @HostBinding('class.disabled') disabled: boolean = false

  DEFAULT_SETTINGS = {
    forceSelection: false,
    match: 'text',
    fields: {
      name: 'name',
      value: 'value'
    },
    fullTextSearch: 'exact',
    onChange: this.onDropdownChange.bind(this)
  }

  onChange: Function | null
  settingValue: boolean

  constructor(
    public elementRef: ElementRef,
    protected ngZone: NgZone,
    protected changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.settings || !this.dropdownSettings) {
      this.dropdownSettings = Object.mergeAll({}, [this.DEFAULT_SETTINGS, (this.settings || {})], { deep: true })
    }

    if (this.options) {
      if (this.options.length > 0) {
        this.ensureNames()
      }
      this.createItems()
      this.trySetDefaultSelected()
    }

    if (changes.setDisabled) {
      this.setDisabledState(this.setDisabled);
    }
  }

  ngAfterViewInit() {
    this.$element = $(this.elementRef.nativeElement)
    this.ngZone.runOutsideAngular(() => {
      this.$element.dropdown(this.dropdownSettings)
    })
    this.trySetDefaultSelected()
  }

  writeValue(modelValue: any) {
    if (modelValue != null) {
      if (this.options && this.$element) {
        this.setSelected(modelValue)
      } else {
        this.defaultSelected = modelValue
      }
    } else if (this.$element) {
      this.$element.dropdown('clear')
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled
    this.changeDetectorRef.markForCheck()
  }

  registerOnTouched(fn: any) { }

  fireOnChange(value) {
    if (this.onChange && !this.settingValue) {
      const index = parseInt(value)
      const choice = this.options[index]
      this.onChange(choice)
      this.modelChange.emit(choice)
    }

    this.settingValue = false;
  }

  setSelected(modelValue) {
    if (this.items) {
      setTimeout(() => {
        const itemValue = this.getItemValue(modelValue)
        if (itemValue == null) {
          this.$element.dropdown('clear')
        } else {
          this.$element.dropdown('set selected', itemValue)
        }
        this.defaultSelected = null
      })
      this.settingValue = true
    }
  }

  ensureNames() {
    const names = this.options.map(o => this.getName(o))
    if (names.compact().length === 0) { throw new Error(`Unable to parse names using ${this.dropdownSettings['fields']['name']}`) }
  }

  createItems() {
    this.items = this.options.map((o, i) => {
      let obj = {
        name: this.getName(o),
        value: `${i}`
      }
      if (o.image) { obj['image'] = o.image }
      if (o.icon) { obj['icon'] = o.icon }
      if (o.color) { obj['color'] = o.color }
      if (o.optgroup) { obj['optgroup'] = o.optgroup }
      if (o.class) { obj['class'] = o.class }
      if (o.description) { obj['description'] = o.description }
      if (o.descriptionIcon) { obj['descriptionIcon'] = o.descriptionIcon }
      return obj
    })
  }

  getItemValue(modelValue) {
    if (!this.options || !this.dropdownSettings) {
      return
    }
    let dropdownValue = Object.get(modelValue, this.dropdownSettings['fields']['value'])
    const index = this.options.findIndex(o => {
      return (
        o === modelValue ||
        Object.isEqual(o, modelValue) ||
        (this.getValue(o) != null && this.getValue(o) === dropdownValue)
      )
    })
    return this.items[index] && this.items[index].value
  }

  getName(option) {
    const nameKey = this.dropdownSettings['fields']['name']
    return option[nameKey] || Object.get(option, nameKey)
  }

  getValue(option) {
    const valueKey = this.dropdownSettings['fields']['value']
    return option[valueKey] || Object.get(option, valueKey)
  }

  trySetDefaultSelected() {
    if (this.defaultSelected) {
      this.setSelected(this.defaultSelected)
    }
  }

  onDropdownChange(value) {
    this.ngZone.run(() => {
      this.fireOnChange(value)
    })
  }

  isEditable(item) {
    return (!this.editableOptions || this.editableOptions.findIndex(it => it.displayName === item.name || it.name === item.name) !== -1)
  }
}
